import { Alert, Button, Input, Pagination, Table, Tag, message } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { TbFolderCog } from 'react-icons/tb'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { withDiskFeature, formatBytes, pluralUk } from '@/Containers/DiskSection/DiskContainer/Hooks'
import { formatInt } from '@/Ship/Components/Dashboard/Overview/shared'
import { Hint } from '@/Ship/Components/Hint'
import { classNames } from '@/Ship/Helpers/classNames'

import { DISK_ADMIN_FILTERS, DISK_ADMIN_QUOTA_WARN_PERCENT } from '../Contracts/DiskAdmin'
import { QuotaBar } from '../Components/DiskAdminCharts'
import { DiskAdminShell } from '../Components/DiskAdminShell'
import { QuotaModal } from '../Components/QuotaModal'
import { StorageBadges, StorageName } from '../Components/StorageName'
import { activityLabel, formatDate } from '../Helpers/diskAdminFormat'
import { diskAdminStorageUrl } from '../Helpers/diskAdminRoutes'
import { diskPolicyStore } from '../Stores/DiskPolicyStore'
import {
    STORAGES_PAGE_SIZE,
    diskAdminStoragesStore as store,
    parseStoragesState,
    storagesStateToParams,
} from '../Stores/DiskAdminStoragesStore'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'
import type { DiskAdminFilter, DiskAdminSort, DiskAdminStorageRow } from '../Contracts/DiskAdmin'
import type { DiskAdminStoragesState } from '../Stores/DiskAdminStoragesStore'
import type { TableColumnsType, TableProps } from 'antd'
import type { HTMLAttributes } from 'react'

import styles from '../Components/DiskAdmin.module.css'

/** Підписи чипів; «неактивні» — з порогом політики, який віддає бек. */
const filterLabel = (filter: DiskAdminFilter, inactiveDays: number | null): string => {
    switch (filter) {
        case 'all':
            return 'Усі'
        case 'personal':
            return 'Особисті'
        case 'shared':
            return 'Спільні'
        case 'departed':
            return 'Звільнені'
        case 'over80':
            return `Понад ${DISK_ADMIN_QUOTA_WARN_PERCENT} %`
        case 'overQuota':
            return 'Понад квоту'
        case 'inactive':
            return inactiveDays !== null ? `Неактивні ${inactiveDays}+ дн.` : 'Неактивні'
        case 'noDisk':
            return 'Без диска'
    }
}

/** Колонки, за якими сортує бек; ключ колонки = значення `sort`. */
const SORTABLE: Partial<Record<string, DiskAdminSort>> = {
    title: 'title',
    used: 'used',
    files: 'files',
    oldVersions: 'oldVersions',
    trash: 'trash',
    lastActivity: 'lastActivity',
}

const sizeOrDash = (bytes: number): string => (bytes > 0 ? formatBytes(bytes) : '—')

/** Квота нижче зайнятого: які з вибраних тепер не приймають файлів. */
const warnBelowUsed = (count: number): void => {
    if (count > 0) {
        void message.warning(
            `Квота менша за зайняте на ${formatInt(count)} ${pluralUk(count, 'диску', 'дисках', 'дисках')}: нові файли туди не завантажаться`
        )
    }
}

/**
 * Що означає «за замовчуванням» для вибраних: «Мій диск» — квота політики,
 * спільний простір — без власної квоти (діє лише ліміт компанії).
 */
const quotaDefaultLabel = (rows: readonly DiskAdminStorageRow[], personal: number | null): string => {
    const hasPersonal = rows.some((row) => row.type === 'personal')
    const hasShared = rows.some((row) => row.type !== 'personal')
    const personalText = personal !== null ? formatBytes(personal) : 'квота політики'

    if (hasPersonal && hasShared) return `За замовчуванням (особисті — ${personalText}, спільні — без власної квоти)`
    if (hasShared) return 'За замовчуванням (без власної квоти)'

    return `За замовчуванням (${personalText})`
}

const RowCard = ({ row }: { row: DiskAdminStorageRow }) => (
    <Link to={diskAdminStorageUrl(row.key)} className={classNames(styles['card'])} data-row={row.key}>
        <StorageName type={row.type} title={row.title} state={row.state} owner={row.owner} />
        <StorageBadges state={row.state} departed={row.departed} hasDisk={row.hasDisk} />
        <QuotaBar used={row.used + row.reserved} quota={row.quota} percent={row.percent} />
    </Link>
)

/**
 * Список дисків `/disk/admin/storages` (`DISK-006` §3.2, наряд Ф3): рядок на
 * кожного співробітника (зокрема без диска й звільнених) і на кожен спільний
 * простір. Фільтр, пошук, сортування й сторінка — в адресі; лічильники чипів
 * рахує той самий запит, що й таблицю. Вибрані рядки — групова зміна квоти.
 *
 * На телефоні таблиця стає картками (ПІБ, позначки, «зайнято / квота»), а
 * чипи прокручуються вбік у своєму рядку — сторінка вбік не їде.
 */
const StoragesPage = observer(() => {
    const [params, setParams] = useSearchParams()
    const navigate = useNavigate()
    const state = useMemo(() => parseStoragesState(params), [params])
    const [search, setSearch] = useState(state.q)
    const [quotaOpen, setQuotaOpen] = useState(false)

    useEffect(() => {
        store.apply(state)
        setSearch(state.q)
    }, [state])

    // Квота «Мого диска» за замовчуванням — для підпису в модалці квоти
    useEffect(() => {
        if (diskPolicyStore.policy === null && !diskPolicyStore.isLoading) void diskPolicyStore.load()
    }, [])

    const update = (patch: Partial<DiskAdminStoragesState>) =>
        setParams(storagesStateToParams({ ...state, page: 1, ...patch }), { replace: true })

    const selected = store.selectedRows
    const onChange: TableProps<DiskAdminStorageRow>['onChange'] = (_pagination, _filters, sorter) => {
        const single = Array.isArray(sorter) ? sorter[0] : sorter
        const sort = single?.columnKey !== undefined ? SORTABLE[String(single.columnKey)] : undefined

        if (sort && single?.order) update({ sort, order: single.order === 'ascend' ? 'asc' : 'desc' })
        else update({ sort: 'used', order: 'desc' })
    }

    const sortOrder = (key: string) =>
        SORTABLE[key] === state.sort ? (state.order === 'asc' ? ('ascend' as const) : ('descend' as const)) : null

    const columns: TableColumnsType<DiskAdminStorageRow> = [
        {
            key: 'title',
            title: 'Диск',
            sorter: true,
            sortOrder: sortOrder('title'),
            render: (_, row) => <StorageName type={row.type} title={row.title} state={row.state} owner={row.owner} />,
            ellipsis: true,
        },
        {
            key: 'state',
            title: 'Стан',
            width: 170,
            render: (_, row) => (
                <span>
                    <StorageBadges state={row.state} departed={row.departed} hasDisk={row.hasDisk} />
                    {row.offboarding?.dueAt ? (
                        <div className={classNames(styles['nameSub'])}>
                            рішення до {formatDate(row.offboarding.dueAt)}
                            {row.offboarding.overdue ? (
                                <Tag color="red" style={{ marginLeft: 6 }}>
                                    прострочено
                                </Tag>
                            ) : null}
                        </div>
                    ) : null}
                    {row.state === 'active' && !row.departed && row.hasDisk ? (
                        <span className={classNames(styles['nameSub'])}>Активний</span>
                    ) : null}
                </span>
            ),
        },
        {
            key: 'used',
            title: 'Зайнято / квота',
            width: 210,
            sorter: true,
            sortOrder: sortOrder('used'),
            render: (_, row) => <QuotaBar used={row.used + row.reserved} quota={row.quota} percent={row.percent} />,
        },
        {
            key: 'files',
            title: 'Файлів',
            width: 100,
            align: 'right',
            sorter: true,
            sortOrder: sortOrder('files'),
            render: (_, row) => <span className={classNames(styles['num'])}>{row.hasDisk ? formatInt(row.files) : '—'}</span>,
        },
        {
            key: 'oldVersions',
            title: 'Старі версії',
            width: 120,
            align: 'right',
            sorter: true,
            sortOrder: sortOrder('oldVersions'),
            render: (_, row) => <span className={classNames(styles['num'])}>{sizeOrDash(row.oldVersions)}</span>,
            responsive: ['lg'],
        },
        {
            key: 'trash',
            title: 'Кошик',
            width: 110,
            align: 'right',
            sorter: true,
            sortOrder: sortOrder('trash'),
            render: (_, row) => <span className={classNames(styles['num'])}>{sizeOrDash(row.trash)}</span>,
            responsive: ['lg'],
        },
        {
            key: 'lastActivity',
            title: 'Активність',
            width: 130,
            sorter: true,
            sortOrder: sortOrder('lastActivity'),
            render: (_, row) => (
                <span className={classNames(styles['num'])} title={formatDate(row.lastActivityAt)}>
                    {activityLabel(row.lastActivityAt)}
                    {row.inactive ? <div className={classNames(styles['nameSub'])}>неактивний</div> : null}
                </span>
            ),
            responsive: ['md'],
        },
    ]

    const counts = store.counts

    return (
        <DiskAdminShell tab="storages" documentTitle="Диски — адміністрування диска" eyebrow="Особисті диски співробітників і спільні простори">
            <Hint id="disk.admin.privacy">
                Склад «Мого диска» адміністратор бачить лише після звільнення співробітника: до того — тільки обсяги,
                квоту й активність. Перегляд складу диска звільненого й кожне відкриття файлу з нього записуються в
                журнал.
            </Hint>

            <div className={classNames(styles['toolbar'])}>
                <div className={classNames(styles['chips'])} role="tablist" aria-label="Фільтр дисків" data-testid="disk-admin-chips">
                    {DISK_ADMIN_FILTERS.map((filter) => (
                        <Button
                            key={filter}
                            role="tab"
                            aria-selected={state.filter === filter}
                            type={state.filter === filter ? 'primary' : 'default'}
                            shape="round"
                            onClick={() => update({ filter })}
                            data-filter={filter}
                        >
                            {filterLabel(filter, store.inactiveDays)}
                            {counts ? <b style={{ marginLeft: 6 }}>{formatInt(counts[filter])}</b> : null}
                        </Button>
                    ))}
                </div>
                <Input.Search
                    className={classNames(styles['search'])}
                    allowClear
                    placeholder="ПІБ або простір"
                    aria-label="Пошук дисків"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onSearch={(value) => update({ q: value })}
                />
            </div>

            {selected.length > 0 ? (
                <div className={classNames(styles['selectionBar'])} data-testid="disk-admin-selection">
                    <span>
                        Вибрано {formatInt(selected.length)}
                        {selected.some((row) => !row.hasDisk) ? ' · для співробітників без диска його буде створено' : ''}
                    </span>
                    <Button type="primary" onClick={() => setQuotaOpen(true)}>
                        Змінити квоту
                    </Button>
                    <Button onClick={() => store.clearSelection()}>Зняти вибір</Button>
                </div>
            ) : null}

            {store.error !== null ? (
                <Alert
                    type="error"
                    showIcon
                    title={store.error}
                    action={
                        <Button size="small" onClick={() => void store.load()}>
                            Повторити
                        </Button>
                    }
                />
            ) : null}

            <div className={classNames(styles['table'])}>
                <Table<DiskAdminStorageRow>
                    rowKey="key"
                    size="middle"
                    columns={columns}
                    dataSource={store.rows}
                    loading={store.isLoading}
                    pagination={false}
                    onChange={onChange}
                    rowSelection={{
                        selectedRowKeys: selected.map((row) => row.key),
                        onChange: (keys) => store.setSelectedOnPage(keys.map(String)),
                        getCheckboxProps: (row) => ({ title: `Вибрати: ${row.title}` }),
                    }}
                    onRow={(row) =>
                        ({
                            onClick: (event) => {
                                if ((event.target as HTMLElement).closest('.ant-table-selection-column')) return
                                navigate(diskAdminStorageUrl(row.key))
                            },
                            style: { cursor: 'pointer' },
                            'data-row': row.key,
                        }) as HTMLAttributes<HTMLElement>
                    }
                    locale={{
                        emptyText: state.q !== '' || state.filter !== 'all' ? 'Нічого не знайдено — змініть фільтр' : 'Дисків ще немає',
                    }}
                    scroll={{ x: 900 }}
                />
            </div>

            <div className={classNames(styles['cards'])}>
                {store.rows.map((row) => (
                    <RowCard key={row.key} row={row} />
                ))}
                {!store.isLoading && store.rows.length === 0 ? (
                    <div className={classNames(styles['nameSub'])}>Нічого не знайдено — змініть фільтр</div>
                ) : null}
            </div>

            {store.total > STORAGES_PAGE_SIZE ? (
                <Pagination
                    align="end"
                    current={state.page}
                    pageSize={STORAGES_PAGE_SIZE}
                    total={store.total}
                    showSizeChanger={false}
                    showTotal={(total, [from, to]) => `${from}–${to} із ${total}`}
                    onChange={(page) => setParams(storagesStateToParams({ ...state, page }), { replace: true })}
                />
            ) : null}

            <QuotaModal
                open={quotaOpen}
                subject={`${formatInt(selected.length)} ${pluralUk(selected.length, 'диск', 'диски', 'дисків')}: ${selected
                    .slice(0, 3)
                    .map((row) => row.title)
                    .join(', ')}${selected.length > 3 ? '…' : ''}`}
                targets={{
                    storages: selected.filter((row) => row.uuid !== null).map((row) => row.uuid as string),
                    users: selected.filter((row) => row.uuid === null && row.owner?.uuid).map((row) => row.owner?.uuid as string),
                }}
                used={selected.length === 1 ? (selected[0]?.used ?? 0) + (selected[0]?.reserved ?? 0) : null}
                currentQuota={selected.length === 1 && selected[0] && !selected[0].quotaIsDefault ? selected[0].quota : null}
                defaultLabel={quotaDefaultLabel(selected, diskPolicyStore.policy?.policy.personalQuotaBytes ?? null)}
                defaultQuota={diskPolicyStore.policy?.policy.personalQuotaBytes ?? null}
                onClose={() => setQuotaOpen(false)}
                onSaved={(result) => {
                    setQuotaOpen(false)
                    void message.success(`Квоту змінено: ${formatInt(result.updated)}`)
                    warnBelowUsed(result.warnings.length)
                    store.clearSelection()
                    void store.load()
                }}
            />
        </DiskAdminShell>
    )
})

export const pageConfig: IPageConfig = {
    key: 'Disk.AdminStorages',
    route: '/disk/admin/storages',
    title: 'Диски — адміністрування диска',
    icon: <TbFolderCog />,
}

export default withDiskFeature(StoragesPage)
